import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ServiceFAQ } from '@/components/sections/ServiceFAQ';
import { useJsonLd } from '@/hooks/useJsonLd';

const entityFAQs = [
  { q: "Should I form an LLC or an S-Corp for my business?", a: "It depends on your revenue, profit margin, and how you pay yourself. An LLC is simpler to maintain but you pay self-employment tax on all profits. An S-Corp lets you split income between salary and distributions, potentially saving $5,000-$20,000+ per year in self-employment tax. KGOB runs the numbers for your specific situation to determine the optimal structure." },
  { q: "How much does it cost to form an LLC or S-Corp in North Carolina?", a: "North Carolina LLC formation costs $125 in state filing fees. S-Corp election (Form 2553) is free to file with the IRS. However, the real cost consideration is ongoing compliance — annual reports, registered agent fees, payroll setup for S-Corps, and maintaining corporate formalities. KGOB handles the full formation process and ongoing compliance from our Charlotte office, serving clients in NC and nationwide." },
  { q: "When should I restructure my business entity?", a: "Consider restructuring when your annual net profit consistently exceeds $50,000-$60,000 (the S-Corp breakpoint), when you're adding partners or investors, when liability protection needs change, or when you're planning for an exit or sale. The wrong structure costs real money every year in unnecessary taxes." },
  { q: "Can I change my LLC to an S-Corp without forming a new entity?", a: "Yes. An existing LLC can elect S-Corp tax treatment by filing IRS Form 2553 — you keep your same EIN, bank accounts, and contracts. The election can be made effective at the beginning of the current tax year if filed within 75 days, or for the following year. KGOB handles the election, payroll setup, and reasonable compensation determination." },
  { q: "What's the difference between an S-Corp and a C-Corp?", a: "S-Corps pass income through to your personal return (single layer of tax). C-Corps pay corporate tax and shareholders pay again on dividends (double taxation). Most small businesses benefit from S-Corp treatment. C-Corps make sense for companies planning to raise outside investment, retain significant earnings, or go public." }
];

const EntityServices = () => {
    useJsonLd('entity-0', {"@context": "https://schema.org", "@type": "Service", "name": "Entity Formation, Registration & Restructuring", "description": "LLC, S-Corp, C-Corp formation, business registration, and entity restructuring for optimal tax efficiency. EIN registration, S-Corp election (Form 2553), state compliance, and reasonable compensation determination.", "url": "https://kgob.com/services/entity", "provider": {"@type": "AccountingService", "name": "KGOB CPAs & Advisors", "url": "https://kgob.com", "address": {"@type": "PostalAddress", "addressLocality": "Charlotte", "addressRegion": "NC", "addressCountry": "US"}}, "serviceType": ["LLC Formation", "S-Corp Election", "C-Corp Formation", "Entity Restructuring", "EIN Registration", "Business Formation"], "areaServed": {"@type": "Country", "name": "United States"}});

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Business Entity Formation & Registration | LLC, S-Corp, Restructuring | KGOB</title>
        <meta name="description" content="Business registration and entity formation guidance for LLC, S-Corp, and restructuring, built for tax savings and long-term growth with KGOB CPAs." />
        <link rel="canonical" href="https://kgob.com/services/entity" />
        <meta property="og:title" content="Business Entity Formation & Registration | LLC, S-Corp, Restructuring | KGOB" />
        <meta property="og:description" content="LLC, S-Corp, C-Corp formation and restructuring. Get guidance on entity selection for optimal tax efficiency." />
        <meta property="og:url" content="https://kgob.com/services/entity" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="flex justify-center mb-6">
                <Building className="w-16 h-16 text-[#D4AF37]" />
              </div>
              
              <h1 className="font-['Playfair_Display'] text-5xl font-bold text-[#F7F6F2] mb-6">
                Entity Formation, Registration & Restructuring
              </h1>
              
              <p className="text-xl text-[#D4AF37] italic mb-4">
                Start right. Structure for savings and growth.
              </p>
              
              <p className="text-lg text-[#F7F6F2]/90 mb-6">
                KGOB guides business registration, incorporation, and entity restructuring so you choose the right structure the first time. We help with LLC and S-Corp decisions, elections, and changes that affect tax and liability.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Button data-testid="entity-services-button-4" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <Link data-testid="entity-services-link-4" to="/book-consultation?source=services-entity">Compare Entity Options</Link>
                </Button>
                <Button data-testid="entity-services-button-3" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                  <Link data-testid="entity-services-link-3" to="/services">View All Services</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Keyword focus */}
        <section className="py-16 bg-white" data-testid="entity-keyword-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-[#0B2240] mb-4">
              Business registration and incorporation guidance
            </h2>
            <p className="text-lg text-[#4C5A70] max-w-3xl mx-auto">
              From new LLC registration and business incorporation services to S-Corp elections and restructuring, we align your structure with your tax plan rather than treating it as paperwork.
            </p>
          </div>
        </section>

        {/* Is This Right for You? */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Is This Right for You?
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7]">
              <p className="text-[#4C5A70] mb-6">
                This service is designed for business owners starting a new venture or considering restructuring their existing business. You will benefit most if:
              </p>
              
              <div className="space-y-3">
                {[
                  'You are starting a new business and need to choose the right entity structure',
                  'You are currently a sole proprietor and want to understand LLC or S corporation benefits',
                  'You are considering an S election to reduce self-employment tax',
                  'You need guidance on multi-member partnerships or multi-entity structures',
                  'You want to ensure your entity choice aligns with long-term tax and exit planning'
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4C5A70]">{item}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* What You Get */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What You Get
            </h2>
            
            <div className="space-y-8">
              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Entity Selection Guidance</h3>
                <p className="text-[#4C5A70] mb-4">
                  We help you compare entity options (LLC, S corporation, partnership, C corporation) and choose the structure that fits your business, tax situation, and long-term goals.
                </p>
                <ul className="space-y-2">
                  {[
                    'Tax impact analysis for each entity type',
                    'Liability protection considerations',
                    'Exit and succession planning alignment',
                    'Coordination with your attorney for legal formation'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">S Election Support</h3>
                <p className="text-[#4C5A70] mb-4">
                  If an S corporation election is right for you, we prepare and file Form 2553 and guide you through the ongoing requirements.
                </p>
                <ul className="space-y-2">
                  {[
                    'S election eligibility review',
                    'Form 2553 preparation and filing',
                    'Reasonable compensation guidance',
                    'Payroll setup coordination'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">EIN & State Registration</h3>
                <p className="text-[#4C5A70] mb-4">
                  We assist with obtaining your federal Employer Identification Number (EIN) and basic state tax registrations.
                </p>
                <ul className="space-y-2">
                  {[
                    'Federal EIN application (Form SS-4)',
                    'State tax ID and sales tax registration',
                    'Franchise tax and annual report guidance',
                    'Clear explanation of ongoing compliance requirements'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-[#4C5A70]/70 italic mt-4">State and filing fees are always billed separately.</p>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              How It Works
            </h2>
            
            <div className="space-y-6">
              {[
                { step: '1', title: 'Initial Consultation', desc: 'We review your business model, income projections, and long-term goals to identify the best entity structure.' },
                { step: '2', title: 'Entity Comparison', desc: 'We prepare a side-by-side analysis showing tax impact, liability protection, and compliance requirements for each option.' },
                { step: '3', title: 'Formation Coordination', desc: 'We coordinate with your attorney for legal formation and handle federal and state tax registrations.' },
                { step: '4', title: 'Elections & Registrations', desc: 'We file any necessary elections (like S election) and set up your tax accounts.' },
                { step: '5', title: 'Ongoing Compliance', desc: 'We provide guidance on annual filings, franchise taxes, and other requirements to keep your entity in good standing.' }
              ].map((item, i) => (
                <Card key={i} className="p-6 border-2 border-[#E1DFD7]">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-lg">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-2">{item.title}</h3>
                      <p className="text-[#4C5A70]">{item.desc}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Changes */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What Changes
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Functional</h3>
                <p className="text-[#4C5A70] text-sm">
                  Your business is structured correctly, with proper liability protection and tax optimization in place.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Emotional</h3>
                <p className="text-[#4C5A70] text-sm">
                  You feel confident knowing your business foundation is set up correctly from the start.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Strategic</h3>
                <p className="text-[#4C5A70] text-sm">
                  With the right entity structure, you can pursue growth, exit, or succession planning with confidence.
                </p>
              </Card>
            </div>
          </div>
        </section>

        <ServiceFAQ faqs={entityFAQs} />

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] to-[#0f2d4f]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-[#F7F6F2] mb-6">
              Ready to Choose the Right Structure?
            </h2>
            <p className="text-lg text-[#F7F6F2]/90 mb-10">
              Schedule a consultation to discuss your business entity options. We will review your situation and recommend the best path forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button data-testid="entity-services-button-2" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="entity-services-link-2" to="/book-consultation?source=services-entity">Compare Entity Options</Link>
              </Button>
              <Button data-testid="entity-services-button-1" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                <Link data-testid="entity-services-link-1" to="/services">View All Services</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default EntityServices;